import type { MetricTemplate } from "~/models/metric";
import { isString } from "~/tools/is-string";
import type { MergeAll } from "~/tools/merge-all";
import { Template } from "~/tools/template";

export interface MetricQuery {
	promql: string;

	/**
	 * When number - the step in milliseconds.
	 * When string - the step as Prometheus duration string (e.g., "15s", "1m", "1h").
	 */
	step?: number | string;

	label?: MetricTemplate;

	componentIdentifier?: MetricTemplate;
}

type MetricQueryPromQLInitVariants = { query: string } | { expression: string } | { promql: string };

function initPromQL(init: MetricQueryPromQLInitVariants): string {
	const promql = "expression" in init ? init.expression : "query" in init ? init.query : init.promql;

	return promql.replace(/\s+/gu, " ").trim();
}

type MetricQueryStepInitVariants = { step?: number | string } | { minStep?: number | string };

function initStep(init: MetricQueryStepInitVariants): undefined | number | string {
	return "step" in init ? init.step : "minStep" in init ? init.minStep : undefined;
}

type MetricQueryLabelInitVariants = { alias?: string | MetricTemplate } | { label?: string | MetricTemplate };

function initLabel(init: MetricQueryLabelInitVariants): undefined | MetricTemplate {
	const alias = "label" in init ? init.label : "alias" in init ? init.alias : undefined;

	if (!alias) {
		return undefined;
	}

	return isString(alias) ? Template(alias) : alias;
}

type MetricQueryComponentIdentifierInitVariants =
	| { componentIdentifierTemplate?: string | MetricTemplate }
	| { componentIdentifier?: string | MetricTemplate };

function initComponentIdentifier(init: MetricQueryComponentIdentifierInitVariants): undefined | MetricTemplate {
	const componentIdentifier =
		"componentIdentifierTemplate" in init
			? init.componentIdentifierTemplate
			: "componentIdentifier" in init
				? init.componentIdentifier
				: undefined;

	if (!componentIdentifier) {
		return undefined;
	}

	return isString(componentIdentifier) ? Template(componentIdentifier) : componentIdentifier;
}

/**
 * The init parameters combine all possible initialization variants for ease of use.
 */
export type MetricQueryInit = MergeAll<
	MetricQueryPromQLInitVariants,
	[MetricQueryStepInitVariants, MetricQueryComponentIdentifierInitVariants, MetricQueryLabelInitVariants]
>;

/**
 * Creates a MetricQuery object.
 * It normalizes the incoming parameters:
 * 1. promql/query/expression -> promql
 * 2. step/minStep -> step
 * 3. alias/label -> label: Template
 *
 * @param init an object combining all possible initialization variants
 *
 * @returns a normalized MetricQuery object
 */
export function MetricQuery(init: MetricQueryInit): MetricQuery {
	return {
		promql: initPromQL(init),
		step: initStep(init),
		label: initLabel(init),
		componentIdentifier: initComponentIdentifier(init),
	};
}
